import { css, Mode, styled } from 'canoe';

import Theme from 'canoe/types/theme';

export const ToggleContainer = styled.div<{
  isActive: boolean;
  disabled: boolean;
  isExpanded?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isInGroup?: boolean;
  isExpandable?: boolean;
  hasBoxLabel?: boolean;
  hasLabel?: boolean;
  variant: 'default' | 'tabs';
  color: 'default' | 'light' | 'orange' | 'grey';
  appearance: 'default' | 'borderless';
  responsiveWidth?: boolean;
  mode?: Mode;
}>`
  ${({
    theme,
    isActive,
    disabled,
    isExpanded,
    isFirst,
    isLast,
    isInGroup,
    isExpandable,
    hasBoxLabel,
    variant,
    color,
    appearance,
    responsiveWidth,
    mode,
  }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    ${
      !responsiveWidth &&
      css`
        width: 32px;
      `
    }
    ${
      !hasBoxLabel &&
      responsiveWidth &&
      css`
        flex-grow: 1;
      `
    }
    cursor: pointer;
    ${
      disabled &&
      css`
        cursor: not-allowed;
      `
    }
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid ${theme.colors.GlitterGray};
    border-right-width: 0;
    transition: background-color 0.4s ease, color 0.4s ease;

    ${
      !isActive &&
      css`
        ${inactiveStyles({ theme })};
      `
    }
    ${
      isActive &&
      css`
        ${activeStyles({ theme })};
      `
    }
    &:last-of-type {
      border-top-right-radius: ${theme.borderRadius.small};
      border-bottom-right-radius: ${theme.borderRadius.small};
      border-right-width: 1px;
    }

    ${
      !hasBoxLabel &&
      css`
        &:first-of-type {
          border-top-left-radius: ${theme.borderRadius.small};
          border-bottom-left-radius: ${theme.borderRadius.small};
          border-left-width: 1px;
        }
      `
    }

    ${
      isInGroup &&
      css`
        ${groupStyles({ isFirst, isLast, hasBoxLabel })};
      `
    }

    ${
      isExpandable &&
      css`
        ${expandableStyles({ isExpanded, hasBoxLabel })};
      `
    }

    ${
      mode === 'Dark' &&
      css`
        ${darkStyles({ theme, isActive })};
      `
    }

    ${
      variant === 'tabs' &&
      css`
        ${tabsOverrides({ theme, isActive })};
      `
    }

    ${
      color === 'orange' &&
      css`
        ${orangeOverrides({ theme, isActive })};
      `
    }

    ${
      color === 'grey' &&
      css`
        ${greyOverrides({ theme, isActive, disabled })};
      `
    }

    ${
      appearance === 'borderless' &&
      css`
        border-color: transparent;

        &,
        &.selected,
        &.draggedOver {
          // override default craft styles making the tabs
          // look broken when used inside the editor
          outline: none;
        }
      `
    }
  `}
`;

const expandableStyles = ({
  isExpanded,
  hasBoxLabel,
}: {
  isExpanded?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  ${isExpanded &&
  css`
    &:last-of-type {
      border-bottom-right-radius: 0;
    }

    ${!hasBoxLabel &&
    css`
      &:first-of-type {
        border-bottom-left-radius: 0;
      }
    `}
  `}
`;

const darkStyles = ({ theme, isActive }: { theme: Theme; isActive?: boolean }) => css`
  background-color: ${theme.colors.OnyxGray};
  border-color: ${theme.colors.OnyxGrayL10};
  color: ${theme.colors.White};

  &:hover {
    color: ${theme.colors.GlitterGrayL20};
  }

  ${isActive &&
  css`
    background-color: ${theme.colors.Success};

    &:hover {
      color: ${theme.colors.GlitterGrayL20};
    }
  `}
`;

const groupStyles = ({
  isFirst,
  isLast,
  hasBoxLabel,
}: {
  isFirst?: boolean;
  isLast?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  &:last-of-type {

    ${
      isFirst &&
      css`
        border-bottom-right-radius: 0;
      `
    }
    ${
      isLast &&
      css`
        border-top-right-radius: 0;
      `
    }

    ${
      !isFirst &&
      !isLast &&
      css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      `
    }
  }

  ${
    !hasBoxLabel &&
    css`
      &:first-of-type {
        ${
          isFirst &&
          css`
            border-bottom-left-radius: 0;
          `
        }
        ${
          isLast &&
          css`
            border-top-left-radius: 0;
          `
        }

        ${
          !isFirst &&
          !isLast &&
          css`
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          `
        }
      }
    `
  }
`;

const inactiveStyles = ({ theme }: { theme: Theme }) => css`
  background-color: ${theme.colors.White};
  color: ${theme.colors.OnyxGray};

  svg * {
    transition: fill 0.4s ease, stroke 0.4s ease;
  }

  &:hover {
    background-color: ${theme.colors.GlitterGrayL10};
  }
`;

const activeStyles = ({ theme }: { theme: Theme }) => css`
  background-color: ${theme.colors.BrandPurple};
  color: ${theme.colors.White};

  &:hover {
    color: ${theme.colors.GlitterGrayL20};
  }
`;

const orangeOverrides = ({ theme, isActive }: { theme: Theme; isActive?: boolean }) => css`
  ${!isActive &&
  css`
    background-color: ${theme.colors.GlitterGrayL30};
    border-color: ${theme.colors.GlitterGray};
    color: ${theme.colors.OnyxGrayL20};

    &:hover {
      color: ${theme.colors.GlitterGrayL10};
    }
  `}

  ${isActive &&
  css`
    background-color: ${theme.colors.MangoOrange};
  `}
`;

const greyOverrides = ({
  theme,
  isActive,
  mode,
  disabled,
}: {
  theme: Theme;
  isActive?: boolean;
  mode?: Mode;
  disabled: boolean;
}) => css`
  ${!isActive &&
  css`
    background-color: ${theme.colors.GlitterGrayL10};
    color: ${theme.colors.OnyxGrayL30};

    ${!disabled &&
    css`
      &:hover {
        background-color: ${theme.colors.GlitterGray};
        color: ${theme.colors.OnyxGray};
      }
    `}

    ${mode === 'Dark' &&
    css`
      background-color: ${theme.colors.OnyxGrayL10};
      color: ${theme.colors.GlitterGrayD10};

      ${!disabled &&
      css`
        &:hover {
          background-color: ${theme.colors.OnyxGray};
          color: ${theme.colors.GlitterGray};
        }
      `}
    `}
  `}

  ${isActive &&
  css`
    &,
    &:hover {
      background-color: ${theme.colors.GlitterGrayD10};
      color: ${theme.colors.OnyxGrayD10};
    }

    ${mode === 'Dark' &&
    css`
      &,
      &:hover {
        background-color: ${theme.colors.GlitterGrayL20};
        color: ${theme.colors.OnyxGray};
      }
    `}
  `}
`;

const tabsOverrides = ({ theme, isActive }: { theme: Theme; isActive?: boolean }) => css`
  font-weight: ${theme.fontWeights.bold};

  ${!isActive &&
  css`
    color: ${theme.colors.OnyxGrayD10};
  `}

  ${isActive &&
  css`
    color: ${theme.colors.GlitterGrayL30};
  `}
`;
